import { useState } from "react";
import { toast } from "sonner";
import { ArrowRight, Loader2, Sparkles, Wrench } from "lucide-react";
import { analyzeHiringScan } from "@/lib/api";
import HiringScanReport from "@/components/HiringScanReport";
import Seo from "@/components/Seo";

/**
 * Internal Hiring Scan generator (sales-team use).
 *
 * Same AI report as the public `/hiring-scan`, but stripped of:
 * - contact gate (no name / email / phone required)
 * - CRM / GHL sync (no `createContact`)
 * - Meta Pixel `Lead` event (no ad-side conversion)
 *
 * Not linked from the public site. Route is `noindex`.
 */
const EMPTY_SCAN = {
  ruolo: "",
  seniority: "Senior",
  settore: "",
  area: "",
  dimensione_azienda: "",
};

export default function InternalHiringScan() {
  const [scan, setScan] = useState(EMPTY_SCAN);
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!scan.ruolo || !scan.settore || !scan.area) {
      toast.error("Compila ruolo, settore e area geografica");
      return;
    }
    setLoading(true);
    try {
      const result = await analyzeHiringScan(scan);
      setReport(result);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error("Errore nella generazione. Riprova.");
    } finally {
      setLoading(false);
    }
  };

  const resetAll = () => {
    setScan(EMPTY_SCAN);
    setReport(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (report) {
    return (
      <HiringScanReport
        scan={scan}
        report={report}
        fee={null}
        onRestart={resetAll}
      />
    );
  }

  return (
    <>
      <Seo
        title="Hiring Scan interno, Lovac"
        description="Tool interno Lovac per generare un Hiring Scan senza raccolta dati contatto."
        path="/tool/hiring-scan"
        noindex
      />

      {/* MINI-HEADER, solo logo */}
      <div className="bg-lovac-dark border-b border-lovac-dark">
        <div className="container-lovac px-6 py-5 flex items-center">
          <img src="/images/logo-orizzontale.png" alt="Lovac" className="h-7 sm:h-8" />
        </div>
      </div>

      <section className="bg-lovac-dark text-on-dark grain relative overflow-hidden min-h-[80vh]">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(60% 50% at 50% 0%, rgba(0,196,140,0.10) 0%, rgba(0,196,140,0) 60%)",
          }}
        />
        <div className="container-lovac px-6 py-20 lg:py-24 relative z-10 max-w-2xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] uppercase tracking-wider"
            style={{ background: "rgba(0,196,140,0.14)", border: "0.5px solid rgba(0,196,140,0.25)", color: "var(--lovac-accent)" }}>
            <Wrench size={12} /> Tool interno · Sales
          </div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl mt-6 text-on-dark leading-[1.05]">
            Genera un Hiring Scan senza gate.
          </h1>
          <p className="text-on-dark-muted mt-5 text-base leading-relaxed">
            Compila solo i campi funzionali al report. Nessun dato contatto,
            nessun invio al CRM, nessun evento di conversione. Da usare in call
            consulenziale con il cliente.
          </p>

          <form
            id="scan-form"
            onSubmit={onSubmit}
            className="mt-10 card-dark space-y-4"
            data-nocapture="true"
            data-ghl-ignore="true"
            data-lc-ignore="true"
            data-testid="internal-scan-form"
          >
            <div>
              <label className="text-xs text-on-dark-muted">Ruolo cercato</label>
              <input
                data-testid="internal-scan-role"
                className="input-on-dark mt-1.5"
                placeholder="es. Architetto Software"
                value={scan.ruolo}
                onChange={(e) => setScan({ ...scan, ruolo: e.target.value })}
              />
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-on-dark-muted">Seniority</label>
                <select
                  data-testid="internal-scan-seniority"
                  className="input-on-dark mt-1.5"
                  value={scan.seniority}
                  onChange={(e) => setScan({ ...scan, seniority: e.target.value })}
                >
                  <option>Junior</option>
                  <option>Middle</option>
                  <option>Senior</option>
                  <option>C-level</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-on-dark-muted">Settore aziendale</label>
                <input
                  data-testid="internal-scan-sector"
                  className="input-on-dark mt-1.5"
                  placeholder="es. Software B2B"
                  value={scan.settore}
                  onChange={(e) => setScan({ ...scan, settore: e.target.value })}
                />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-on-dark-muted">Area geografica</label>
                <input
                  data-testid="internal-scan-area"
                  className="input-on-dark mt-1.5"
                  placeholder="es. Marche / Milano"
                  value={scan.area}
                  onChange={(e) => setScan({ ...scan, area: e.target.value })}
                />
              </div>
              <div>
                <label className="text-xs text-on-dark-muted">
                  Dimensione azienda <span className="opacity-60">(opz.)</span>
                </label>
                <select
                  data-testid="internal-scan-size"
                  className="input-on-dark mt-1.5"
                  value={scan.dimensione_azienda}
                  onChange={(e) => setScan({ ...scan, dimensione_azienda: e.target.value })}
                >
                  <option value="">—</option>
                  <option>1–10</option>
                  <option>11–50</option>
                  <option>51–250</option>
                  <option>250+</option>
                </select>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full mt-3 disabled:opacity-60"
              data-testid="internal-scan-submit"
            >
              {loading ? (
                <>
                  <Loader2 size={16} className="animate-spin" /> Genero il report…
                </>
              ) : (
                <>
                  <Sparkles size={16} /> Genera il report <ArrowRight size={16} />
                </>
              )}
            </button>

            <p className="text-[11px] text-on-dark-muted text-center pt-1">
              Uso interno · Nessun dato salvato nel CRM.
            </p>
          </form>
        </div>
      </section>
    </>
  );
}
